import React, { useState } from 'react';
import {
    Container,
    Typography,
    Box,
    Grid2,
    Button,
    Snackbar,
    Alert
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteImageFromStorage, getCurrentBusinessConfig, updateHomepageConfig, uploadImageToStorage } from '../../../firebase/firebase-delegate';
import { BusinessConfig, GalleryItem } from '../../../types';
import AdminGalleryCard from './AdminGalleryCard';
import SelectGalleryImageDialog from './SelectGalleryImageDialog';
import { GALLERY_DB_REF } from '../../../firebase/firebase-references';
import LoadingSpinner from '../../../components/global/LoadingSpinner';
import ConfirmationDialog from '../../../components/global/ConfirmationDialog';

const MyGalleryScreen: React.FC = () => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<GalleryItem | undefined>(undefined);
    const [pendingDeleteItem, setPendingDeleteItem] = useState<GalleryItem | null>(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const queryClient = useQueryClient();

    const businessConfigQuery = useQuery<BusinessConfig | null>({
        queryKey: ['businessConfig'],
        queryFn: () => getCurrentBusinessConfig(),
    });

    const handleOpenAddDialog = () => {
        setEditingItem(undefined);
        setDialogOpen(true);
    };

    const deleteGalleryItemMutation = useMutation({
        mutationFn: async (itemToDelete: GalleryItem) => {
            if (!businessConfigQuery.data || !itemToDelete.imagePath) {
                throw new Error("Invalid item");
            }

            await deleteImageFromStorage(itemToDelete.imagePath);

            const updatedConfig = { ...businessConfigQuery.data };
            const gallery = updatedConfig.homepage.gallery || [];

            const updatedGallery = gallery.filter(
                (item) => item.imagePath !== itemToDelete.imagePath
            );

            updatedConfig.homepage.gallery = updatedGallery;

            await updateHomepageConfig(updatedConfig.homepage);
        },
        onSuccess: () => {
            if (!businessConfigQuery.data || !pendingDeleteItem?.imagePath) return;
            const updatedConfig = { ...businessConfigQuery.data };
            const gallery = updatedConfig.homepage.gallery || [];
            updatedConfig.homepage.gallery = gallery.filter(
                (item) => item.imagePath !== pendingDeleteItem.imagePath
            );
            queryClient.setQueryData(['businessConfig'], updatedConfig);
            setSnackbar({ open: true, message: 'Gallery image deleted.', severity: 'success' });
        },
        onError: (error) => {
            console.error("Failed to delete gallery image:", error);
            setSnackbar({ open: true, message: 'Failed to delete gallery image.', severity: 'error' });
        },
    });



    const saveGalleryItemMutation = useMutation({
        mutationFn: async ({
            file,
            title,
            description,
            editingItem,
        }: {
            file?: File;
            title?: string;
            description?: string;
            editingItem?: GalleryItem;
        }) => {
            if (!businessConfigQuery.data) throw new Error("Missing config");
            if (title && title.length > 100) throw new Error("Title must be under 100 characters");
            if (description && description.length > 200) throw new Error("Description must be under 200 characters");

            const updatedConfig = { ...businessConfigQuery.data };
            const gallery = updatedConfig.homepage.gallery || [];

            let imagePath = editingItem?.imagePath ?? '';
            let imageURL = editingItem?.imageURL ?? '';

            if (file) {
                imagePath = `${GALLERY_DB_REF}/${Date.now()}`;
                imageURL = await uploadImageToStorage(file, imagePath, 1);
            }

            if (!imagePath || !imageURL) {
                throw new Error("Missing image path or URL");
            }

            const trimmedTitle = title?.trim() || undefined;
            const trimmedDescription = description?.trim() || undefined;

            const updatedGalleryItem = {
                imagePath,
                imageURL,
                ...(trimmedTitle ? { title: trimmedTitle } : {}),
                ...(trimmedDescription ? { description: trimmedDescription } : {}),
            };

            const updatedGallery = editingItem?.imagePath
                ? gallery.map((item) =>
                    item.imagePath === editingItem.imagePath ? updatedGalleryItem : item
                )
                : [...gallery, updatedGalleryItem];

            updatedConfig.homepage.gallery = updatedGallery;

            await updateHomepageConfig(updatedConfig.homepage);
            return updatedConfig;
        },
        onSuccess: (updatedConfig) => {
            if (!updatedConfig) return;
            queryClient.setQueryData(['businessConfig'], updatedConfig);
            setSnackbar({ open: true, message: 'Gallery image saved.', severity: 'success' });
        },
        onError: (error: any) => {
            console.error("Failed to save gallery item:", error);
            setSnackbar({ open: true, message: 'Failed to save gallery image.', severity: 'error' });
        },
    });

    const isLoading =
        businessConfigQuery.isLoading ||
        saveGalleryItemMutation.isPending ||
        deleteGalleryItemMutation.isPending;


    if (!businessConfigQuery.data) {
        return <Typography align="center">Loading...</Typography>;
    }

    const galleryItems = businessConfigQuery.data.homepage.gallery || [];

    const handleSave = (file?: File, title?: string, description?: string) => {
        saveGalleryItemMutation.mutate({ file, title, description, editingItem });
    };


    const handleDelete = (item: GalleryItem) => {
        setPendingDeleteItem(item);
        setIsDeleteDialogOpen(true);
    };

    const handleEdit = (item: GalleryItem) => {
        setEditingItem(item);
        setDialogOpen(true);
    };

    const handleCloseEdit = () => {
        setDialogOpen(false);
        setEditingItem(undefined);
    };

    const handleConfirmDelete = () => {
        if (!pendingDeleteItem) return;
        setIsDeleteDialogOpen(false);
        deleteGalleryItemMutation.mutate(pendingDeleteItem);
        if (editingItem?.imagePath === pendingDeleteItem.imagePath) {
            setDialogOpen(false);
            setEditingItem(undefined);
        }
    };

    return (
        <Box sx={{ pb: isMobile ? 10 : 0 }}>
            <Container>
                {!isMobile && (
                    <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleOpenAddDialog}
                            sx={{
                                borderRadius: '4px',
                                border: '2px solid rgba(255,255,255,0.15)',
                                fontWeight: '600',
                                paddingX: 3.5
                            }}
                        >
                            ADD PHOTO
                        </Button>
                    </Box>
                )}

                {!isMobile && (
                    <hr
                        style={{
                            border: "none",
                            height: "2px",
                            backgroundColor: "rgba(0,0,0,0.2)",
                            boxShadow: "0 1px 5px rgba(0, 0, 0, 0.3)",
                            marginBottom: "20px",
                        }}
                    />
                )}

                {isLoading ? (
                    <Box mt={4}>
                        <LoadingSpinner />
                    </Box>
                ) : galleryItems.length === 0 ? (
                    <Box
                        sx={{
                            mt: 3,
                            px: 2,
                            py: 5,
                            textAlign: 'center',
                        }}
                    >
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            Add some photos to get started and showcase your work.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Tap ADD PHOTO to upload your first image.
                        </Typography>
                    </Box>
                ) : (
                    <Grid2 container spacing={2}>

                        {/* Existing gallery items */}
                        {galleryItems.map((item, idx) => (
                            <Grid2 size={{xs:12, sm:6, md:4}} key={idx}>
                                <AdminGalleryCard
                                    item={item}
                                    onDelete={handleDelete}
                                    onEdit={handleEdit}
                                    onCloseEdit={handleCloseEdit}
                                    onSave={handleSave}
                                    isEditing={!isMobile && dialogOpen && editingItem?.imagePath === item.imagePath}
                                />
                            </Grid2>
                        ))}
                    </Grid2>
                )}

                {isMobile && (
                    <Box
                        sx={{
                            position: 'fixed',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            px: 2,
                            py: 1,
                            bgcolor: 'background.paper',
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            boxShadow: 3,
                            zIndex: 10,
                        }}
                    >
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleOpenAddDialog}
                            fullWidth
                            sx={{
                                borderRadius: '4px',
                                border: '2px solid rgba(255,255,255,0.15)',
                                fontWeight: '600',
                            }}
                        >
                            ADD PHOTO
                        </Button>
                    </Box>
                )}

                {(isMobile || !editingItem) && (
                    <SelectGalleryImageDialog
                        open={dialogOpen}
                        onClose={handleCloseEdit}
                        onSave={handleSave}
                        initialItem={editingItem}
                        mode="dialog"
                        onDelete={editingItem ? () => handleDelete(editingItem) : undefined}
                    />
                )}
                <ConfirmationDialog
                    open={isDeleteDialogOpen}
                    onClose={() => setIsDeleteDialogOpen(false)}
                    title="Confirm Deletion"
                    message="Are you sure you want to delete this image? This action cannot be undone."
                    onConfirm={handleConfirmDelete}
                    confirmButtonLabel="Delete"
                    cancelButtonLabel="Cancel"
                    confirmButtonSx={{ color: 'error.main' }}
                    cancelButtonSx={{ color: 'common.black' }}
                />
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </Box>
    );

};

export default MyGalleryScreen;
